import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<Table>` + `<TableRow>` replace `.tbl`, `.tbl-wrap`,
 * `.tbl thead th`, and the priority-bar rows `.p1r` / `.p2r` (critical /
 * warn left border via inset box-shadow in common-ui.css; here we use
 * the cleaner `border-l-*` utility).
 *
 * TableHead is a plain element — consumers lay out `<th>` / `<td>` as
 * usual. `TableRow tone="critical"` / `"warn"` adds the coloured first-
 * column bar.
 */

export function Table({ className, children }) {
  return (
    <div className={cn("overflow-auto rounded-[10px] border border-rc-border bg-rc-surface", className)}>
      <table className="w-full border-collapse">{children}</table>
    </div>
  );
}

export function TableHead({ children, className }) {
  return (
    <thead className={cn("bg-rc-surface-hover", className)}>
      {children}
    </thead>
  );
}

export function TableHeaderCell({ children, sorted, onClick, className }) {
  return (
    <th
      onClick={onClick}
      className={cn(
        "px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.1em] border-b border-rc-border whitespace-nowrap select-none",
        onClick && "cursor-pointer transition-colors hover:text-rc-text-primary",
        sorted ? "text-rc-accent-strong" : "text-rc-text-secondary",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TableBody({ children }) {
  return <tbody>{children}</tbody>;
}

const ROW_TONE = {
  default:  "",
  critical: "[&>td:first-child]:shadow-[inset_3px_0_0_var(--rc-red)]",
  warn:     "[&>td:first-child]:shadow-[inset_3px_0_0_var(--rc-yellow)]",
  info:     "[&>td:first-child]:shadow-[inset_3px_0_0_var(--rc-accent)]",
  success:  "[&>td:first-child]:shadow-[inset_3px_0_0_#10c981]",
};

export function TableRow({ onClick, tone = "default", children, className }) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        "transition-colors",
        onClick && "cursor-pointer hover:bg-rc-surface-hover",
        ROW_TONE[tone],
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function TableCell({ children, className }) {
  return (
    <td className={cn(
      "px-3.5 py-[11px] text-[13px] align-middle text-rc-text-primary border-b border-rc-border",
      "[tr:last-child>&]:border-b-0",
      className,
    )}>
      {children}
    </td>
  );
}

export default Table;
